import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { useMemo } from "react";
import type { EntityScope } from "../../shared/scope";

export function useAdminPreviewsTable<
	TPreview extends { underwriterUserId?: string | null },
	TEnriched extends TPreview & { underwriterName: string } = TPreview & {
		underwriterName: string;
	},
>({
	data,
	filterPredicate,
	enrichRow,
	scope,
}: {
	data: TPreview[] | undefined;
	filterPredicate?: (item: TPreview) => boolean;
	enrichRow?: (item: TPreview, underwriterName: string) => TEnriched;
	scope?: EntityScope;
}) {
	const { user } = useAuthenticatedUser();
	const { adminUsers } = useAdminFetchAdminUsers();

	const enrichedData = useMemo(() => {
		if (!data) return undefined;
		const userNameById = new Map<string, string>(
			adminUsers?.map((u) => [u.id, `${u.firstName} ${u.lastName}`]),
		);
		const filtered = filterPredicate ? data.filter(filterPredicate) : data;
		return filtered.map((item) => {
			const underwriterName = item.underwriterUserId
				? (userNameById.get(item.underwriterUserId) ?? "N/A")
				: "N/A";
			if (enrichRow) return enrichRow(item, underwriterName);
			return { ...item, underwriterName } as TEnriched;
		});
	}, [data, adminUsers, filterPredicate, enrichRow]);

	const userName = `${user.firstName} ${user.lastName}`;

	// Legacy default (no scope selector): pre-select the current user in the
	// underwriter column filter, applied once, only if they have rows.
	const defaultColumnFilters = useMemo<ColumnFiltersState | undefined>(() => {
		if (scope !== undefined) return undefined;
		if (!enrichedData?.some((item) => item.underwriterName === userName)) return undefined;
		return [{ id: "underwriter", value: [userName] }];
	}, [enrichedData, userName, scope]);

	// Scope selector drives the underwriter column filter: "me" selects the
	// current user, "everyone" clears it. Reconciled into the table reactively.
	const scopeColumnFilters = useMemo<ColumnFiltersState | undefined>(() => {
		if (scope === undefined) return undefined;
		return [{ id: "underwriter", value: scope === "me" ? [userName] : [] }];
	}, [scope, userName]);

	return { data: enrichedData, defaultColumnFilters, scopeColumnFilters };
}
